import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PasswordChangeForm } from "./PasswordChangeForm";
import { t } from "i18next";
import { useMultiModalController } from "@/store/multiModalController.store";
import { useForm } from "react-hook-form";
import {
  PasswordChangeSchema,
  type PasswordChangeSchemaType,
} from "@/schemas/passwordChange.schema";
import { AccountSettingsField } from "@/common/enums/accountSettings";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { useAuthStore } from "@/store/auth.store";
import { useUpdateAuthUser } from "@/hooks/user/useUpdateAuthUser";

export const PasswordChangeDialog = (): JSX.Element => {
  const { userData, getUser } = useAuthStore();
  const { modals, setOpenByModalId } = useMultiModalController();
  const { changePassword, loading, result } = useUpdateAuthUser();

  const form = useForm<PasswordChangeSchemaType>({
    resolver: zodResolver(PasswordChangeSchema()),
    mode: "onSubmit",
    shouldUnregister: false,
    reValidateMode: "onSubmit",
    defaultValues: {
      confirmNewPassword: "",
      newPassword: "",
      oldPassword: "",
    },
  });

  const handleSubmitChangePassword = (data: PasswordChangeSchemaType): void => {
    void changePassword(data.newPassword);
  };

  useEffect(() => {
    if (result != null && !loading) {
      if (result) {
        toast({
          variant: "success",
          title: t("settings.account.updatedToast.successful.title"),
          description: t(
            "settings.account.updatedToast.successful.description"
          ),
        });
        setOpenByModalId(AccountSettingsField.CHANGE_PASSWORD, false);
        if (userData) void getUser(userData.email);
      } else {
        toast({
          variant: "destructive",
          title: t("settings.account.updatedToast.error.title"),
          description: t("settings.account.updatedToast.error.description"),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, loading]);

  return (
    <Dialog
      open={modals[AccountSettingsField.CHANGE_PASSWORD]}
      onOpenChange={(open) => {
        setOpenByModalId(AccountSettingsField.CHANGE_PASSWORD, open);
      }}
    >
      <DialogContent>
        <DialogHeader className="space-y-4">
          <DialogTitle className="text-center">
            {t("settings.account.changePasswordLabel")}
          </DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>
        <PasswordChangeForm
          form={form}
          isSubmitting={loading}
          onSubmit={handleSubmitChangePassword}
        />
      </DialogContent>
    </Dialog>
  );
};
